import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, useEffect, useCallback, useMemo } from 'react'

import { get } from '@/lib/api'

// ==================== Types ====================

export type SearchCategory = 'projects' | 'issues' | 'employees'

export interface ProjectSearchResult {
  id: string
  code: string
  name: string
  status: string
  matchedField: 'name' | 'code'
}

export interface IssueSearchResult {
  id: string
  code: string
  title: string
  projectId: string
  projectName: string
  status: string
  matchedField: 'title' | 'code'
}

export interface EmployeeSearchResult {
  id: string
  code: string
  name: string
  departmentName: string | null
  positionName: string | null
  matchedField: 'name' | 'code'
}

export interface SearchResults {
  projects: ProjectSearchResult[]
  issues: IssueSearchResult[]
  employees: EmployeeSearchResult[]
  totals: {
    projects: number
    issues: number
    employees: number
  }
}

export interface SearchResultItem {
  category: SearchCategory
  id: string
  code: string
  title: string
  subtitle?: string
  status?: string
  path: string
}

export interface QuickAction {
  id: string
  label: string
  icon: string
  path: string
  roles: string[]
}

// ==================== Constants ====================

const SEARCH_HISTORY_KEY = 'global-search-history'
const MAX_HISTORY_ITEMS = 5
const DEBOUNCE_MS = 300
const CACHE_TIME_MS = 30000

export const QUICK_ACTIONS: QuickAction[] = [
  {
    id: 'create-task',
    label: '创建任务',
    icon: 'plus-circle',
    path: '/pm/issues/new',
    roles: ['director', 'pm', 'developer', 'leader'],
  },
  {
    id: 'log-time',
    label: '记录工时',
    icon: 'clock',
    path: '/pm/timelogs/new',
    roles: ['director', 'pm', 'developer', 'leader'],
  },
  {
    id: 'create-project',
    label: '创建项目',
    icon: 'folder-plus',
    path: '/pm/projects/new',
    roles: ['director', 'pm'],
  },
  {
    id: 'add-employee',
    label: '添加员工',
    icon: 'user-plus',
    path: '/hr/employees/new',
    roles: ['director', 'hr'],
  },
  {
    id: 'record-cash-flow',
    label: '记录现金流',
    icon: 'dollar-sign',
    path: '/finance/cash-flows/new',
    roles: ['director', 'finance'],
  },
]

// ==================== Query Keys ====================

export const searchKeys = {
  all: ['search'] as const,
  query: (q: string) => [...searchKeys.all, 'query', q] as const,
}

// ==================== Hooks ====================

/**
 * 全局搜索 Hook
 * - 300ms 防抖
 * - 30 秒缓存
 * - 最少 2 字符触发搜索
 */
export function useGlobalSearch(query: string) {
  const [debouncedQuery, setDebouncedQuery] = useState(query)

  // 防抖处理
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query)
    }, DEBOUNCE_MS)

    return () => clearTimeout(timer)
  }, [query])

  const isValidQuery = debouncedQuery.length >= 2

  return useQuery({
    queryKey: searchKeys.query(debouncedQuery),
    queryFn: () => get<SearchResults>('/search', { q: debouncedQuery }),
    enabled: isValidQuery,
    staleTime: CACHE_TIME_MS,
    gcTime: CACHE_TIME_MS * 2,
  })
}

/**
 * 搜索历史管理 Hook
 * - 存储到 localStorage
 * - 最多 5 条
 * - 避免重复
 */
export function useSearchHistory() {
  const [history, setHistory] = useState<string[]>(() => {
    if (typeof window === 'undefined') { return [] }
    try {
      const stored = localStorage.getItem(SEARCH_HISTORY_KEY)
      return stored ? (JSON.parse(stored) as string[]) : []
    } catch {
      return []
    }
  })

  const addSearch = useCallback((query: string) => {
    if (!query || query.length < 2) { return }

    setHistory((prev) => {
      // 移除重复项
      const filtered = prev.filter((item) => item !== query)
      // 添加到开头，限制数量
      const newHistory = [query, ...filtered].slice(0, MAX_HISTORY_ITEMS)

      // 保存到 localStorage
      try {
        localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(newHistory))
      } catch {
        // 忽略存储错误
      }

      return newHistory
    })
  }, [])

  const clearHistory = useCallback(() => {
    setHistory([])
    try {
      localStorage.removeItem(SEARCH_HISTORY_KEY)
    } catch {
      // 忽略存储错误
    }
  }, [])

  return { history, addSearch, clearHistory }
}

/**
 * 搜索对话框状态 Hook
 * - 监听 Cmd+K/Ctrl+K 快捷键
 */
export function useSearchDialog() {
  const [isOpen, setIsOpen] = useState(false)

  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => setIsOpen(false), [])
  const toggle = useCallback(() => setIsOpen((prev) => !prev), [])

  // 监听快捷键
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+K (Mac) 或 Ctrl+K (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        toggle()
      }

      // Escape 关闭
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault()
        close()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, toggle, close])

  return { isOpen, open, close, toggle }
}

/**
 * 将搜索结果转换为统一的列表项格式
 */
export function useSearchResultItems(results: SearchResults | undefined): SearchResultItem[] {
  return useMemo(() => {
    if (!results) { return [] }

    const items: SearchResultItem[] = []

    // 项目
    results.projects.forEach((project) => {
      items.push({
        category: 'projects',
        id: project.id,
        code: project.code,
        title: project.name,
        status: project.status,
        path: `/pm/projects/${project.id}`,
      })
    })

    // 任务
    results.issues.forEach((issue) => {
      items.push({
        category: 'issues',
        id: issue.id,
        code: issue.code,
        title: issue.title,
        subtitle: issue.projectName,
        status: issue.status,
        path: `/pm/issues/${issue.id}`,
      })
    })

    // 员工
    results.employees.forEach((employee) => {
      items.push({
        category: 'employees',
        id: employee.id,
        code: employee.code,
        title: employee.name,
        subtitle: [employee.departmentName, employee.positionName].filter(Boolean).join(' · '),
        path: `/hr/employees/${employee.id}`,
      })
    })

    return items
  }, [results])
}

/**
 * 根据用户角色过滤快捷操作
 */
export function useQuickActions(userRole: string): QuickAction[] {
  return useMemo(() => {
    return QUICK_ACTIONS.filter((action) => action.roles.includes(userRole))
  }, [userRole])
}

/**
 * 预取搜索结果
 */
export function usePrefetchSearch() {
  const queryClient = useQueryClient()

  return useCallback(
    (query: string) => {
      if (query.length >= 2) {
        void queryClient.prefetchQuery({
          queryKey: searchKeys.query(query),
          queryFn: () => get<SearchResults>('/search', { q: query }),
          staleTime: CACHE_TIME_MS,
        })
      }
    },
    [queryClient]
  )
}
